import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import TrustStrip from '../../components/feature/TrustStrip';
import { businessConfig } from '../../config/business';

export default function HomePage() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const openScheduleWidget = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    }
  };

  const whyChooseCards = [
    {
      icon: 'ri-map-2-line',
      title: 'Local South Florida Expertise',
      shortDesc: 'Deep understanding of regional pool care needs and climate considerations',
      expandedContent: businessConfig.expertise.climate
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Dependable Scheduling',
      shortDesc: 'Reliable service routes and flexible scheduling for all property types',
      expandedContent: [
        '<strong>Residential:</strong>',
        ...businessConfig.expertise.scheduling.residential,
        '<strong>Commercial / HOA:</strong>',
        ...businessConfig.expertise.scheduling.commercial
      ]
    },
    {
      icon: 'ri-tools-fill',
      title: 'Repairs & Equipment Expertise',
      shortDesc: 'Comprehensive repair services for all pool equipment and systems',
      expandedContent: [
        '<strong>Pumps & Motors:</strong> Variable-speed diagnostics, motor seal & bearing failures, electrical issues, energy-efficiency upgrades',
        '<strong>Filters:</strong> Pressure & flow issues, cracked housings, grid replacements, deep clean servicing',
        '<strong>Heaters:</strong> Igniters & sensors, flow switches, startup inspections, efficiency assessments',
        '<strong>Valves & Plumbing:</strong> Diverter valves, suction & return diagnostics, leak detection',
        '<strong>Automation & Salt Systems:</strong> Pentair/Hayward/Jandy systems, timer installs, salt cell diagnostics'
      ]
    },
    {
      icon: 'ri-award-line',
      title: 'Professional Standards',
      shortDesc: 'Licensed, insured, and committed to excellence in every service',
      expandedContent: [
        'Licensed & insured professionals',
        'Manufacturer-recommended procedures',
        'Safe chemical handling practices',
        'HOA compliance awareness',
        'Respectful on-site conduct'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white font-['Work_Sans']">
      <Header />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/c78948e59b7e258d49e6a56211ec529d.jpg"
            alt="Professional Pool Service in South Florida"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-2 rounded-full mb-6">
            <span className="text-white font-medium flex items-center space-x-2">
              <i className="ri-map-pin-line"></i>
              <span>{businessConfig.serviceArea.full}</span>
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-['Crimson_Pro'] font-bold text-white mb-6 leading-tight">
            Professional Pool Service<br />You Can Trust
          </h1>
          
          <p className="text-xl md:text-2xl text-white/95 mb-4 max-w-3xl mx-auto font-light leading-relaxed">
            Routine maintenance, preventative care, and expert repairs for residential and commercial pools across South Florida
          </p>

          <p className="text-sm text-white/80 mb-10 flex items-center justify-center space-x-4">
            <span className="flex items-center space-x-1">
              <i className="ri-close-circle-line"></i>
              <span>No contracts</span>
            </span>
            <span className="flex items-center space-x-1">
              <i className="ri-close-circle-line"></i>
              <span>No pressure</span>
            </span>
            <span className="flex items-center space-x-1">
              <i className="ri-checkbox-circle-line text-green-400"></i>
              <span>Free assessment</span>
            </span>
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button 
              onClick={openScheduleWidget}
              className="group bg-[#2563eb] text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-[#1d4ed8] transition-all duration-300 flex items-center space-x-3 hover:shadow-2xl hover:scale-105 whitespace-nowrap w-full sm:w-auto justify-center cursor-pointer"
            >
              <span>Schedule a Free Visit</span>
              <i className="ri-calendar-check-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
            </button>
            
            <Link 
              to="/contact"
              className="group bg-white text-[#0f2942] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center space-x-3 hover:shadow-2xl hover:scale-105 whitespace-nowrap w-full sm:w-auto justify-center"
            >
              <span>Contact Us</span>
              <i className="ri-message-3-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Trust Strip */}
      <TrustStrip />

      {/* Service Type Selection */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-['Crimson_Pro'] font-bold text-[#0f2942] mb-4">
              Choose Your Service Type
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional pool care tailored to your specific needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Residential Card */}
            <div className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20residential%20backyard%20swimming%20pool%20with%20crystal%20clear%20blue%20water%20elegant%20modern%20home%20pool%20design%20sunny%20day%20with%20palm%20trees%20and%20tropical%20plants%20pristine%20water%20quality%20family%20friendly%20residential%20pool%20setting%20clean%20simple%20background%20professional%20pool%20maintenance&width=600&height=400&seq=residential-pool-service&orientation=landscape"
                  alt="Residential Pool Service"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-home-4-line text-3xl text-[#2563eb]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    Residential Pool Service
                  </h3>
                </div>
              </div>

              <div className="p-8">
                <ul className="space-y-3 mb-6">
                  {businessConfig.services.residential.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#2563eb] text-xl mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link 
                  to="/residential"
                  className="w-full bg-[#2563eb] text-white py-3 rounded-lg font-semibold hover:bg-[#1d4ed8] transition-all duration-300 flex items-center justify-center space-x-2 group-hover:shadow-lg whitespace-nowrap"
                >
                  <span>Get Started – Residential</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>

            {/* Commercial Card */}
            <div className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=large%20commercial%20swimming%20pool%20complex%20professional%20pool%20maintenance%20setting%20hotel%20resort%20pool%20with%20multiple%20sections%20clean%20organized%20commercial%20pool%20facility%20bright%20sunny%20day%20with%20palm%20trees%20modern%20commercial%20pool%20design%20pristine%20water%20quality%20simple%20clean%20background&width=600&height=400&seq=commercial-pool-service&orientation=landscape"
                  alt="Commercial Pool Service"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-building-line text-3xl text-[#2563eb]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    Commercial / HOA Pool Service
                  </h3>
                </div>
              </div>

              <div className="p-8">
                <ul className="space-y-3 mb-6">
                  {businessConfig.services.commercial.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#2563eb] text-xl mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link 
                  to="/commercial"
                  className="w-full bg-[#2563eb] text-white py-3 rounded-lg font-semibold hover:bg-[#1d4ed8] transition-all duration-300 flex items-center justify-center space-x-2 group-hover:shadow-lg whitespace-nowrap"
                >
                  <span>Get Started – Commercial</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-['Crimson_Pro'] font-bold text-[#0f2942] mb-4">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive pool care to keep your water crystal clear and equipment running smoothly
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {businessConfig.services.core.map((service, index) => (
              <div 
                key={index}
                className="group bg-white p-8 rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#2563eb] to-[#3b82f6] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-xl font-bold text-[#0f2942] mb-3">
                  {service.name}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Florida Blue Pools */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-['Crimson_Pro'] font-bold text-[#0f2942] mb-4">
              Why Choose Florida Blue Pools
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional pool care backed by local expertise and proven reliability
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyChooseCards.map((card, index) => (
              <div 
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-[#2563eb] transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#2563eb] to-[#3b82f6] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${card.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#0f2942] mb-2">
                      {card.title}
                    </h3>
                    <p className="text-gray-600">
                      {card.shortDesc}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setExpandedCard(expandedCard === index ? null : index)}
                  className="text-[#2563eb] font-semibold flex items-center space-x-2 hover:underline cursor-pointer"
                >
                  <span>{expandedCard === index ? 'Show Less' : 'Learn More'}</span>
                  <i className={`ri-arrow-${expandedCard === index ? 'up' : 'down'}-s-line text-xl`}></i>
                </button>

                {expandedCard === index && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <ul className="space-y-2">
                      {card.expandedContent.map((item, idx) => (
                        <li 
                          key={idx} 
                          className="flex items-start space-x-3 text-gray-700"
                          dangerouslySetInnerHTML={{ __html: item.includes('<strong>') ? item : `<span class="flex items-center"><i class="ri-checkbox-circle-fill text-[#2563eb] mr-2"></i>${item}</span>` }}
                        />
                      ))}
                    </ul>
                    {index === 2 && (
                      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-gray-700 font-semibold">
                          <i className="ri-shield-check-line text-[#2563eb] mr-2"></i>
                          Licensed • Insured • HOA-Compliant Repairs
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Repair Process */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-['Crimson_Pro'] font-bold text-[#0f2942] mb-4">
              How Our Repair Process Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Transparent, professional service from diagnosis to completion
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessConfig.repairProcess.map((step, index) => (
              <div 
                key={index}
                className="relative bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-[#2563eb] to-[#3b82f6] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {step.step}
                </div>
                <h4 className="text-lg font-bold text-[#0f2942] mb-3 mt-4">
                  {step.title}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#0f2942] to-[#1e3a5f] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#2563eb] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#2563eb] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-['Crimson_Pro'] font-bold text-white mb-6">
            Ready to Experience Professional Pool Care?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Schedule your free, no-obligation pool assessment today
          </p>
          <button 
            onClick={openScheduleWidget}
            className="group bg-white text-[#0f2942] px-12 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all duration-300 inline-flex items-center space-x-3 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <span>Schedule Your Free Visit</span>
            <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
